import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {type ConnectionOperation, DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {dispatchConnectionMutation} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import {escapeOptimisticTitleHTML} from '../utils/helpers'
import type {
  UpdateIssueInput,
  updateIssueTitleMutation,
  updateIssueTitleMutation$data,
} from './__generated__/updateIssueTitleMutation.graphql'

/** Packages a mutation response into the `{id, values}` shape the event carries, or
 * `undefined` if the affected node is missing (e.g. an unexpectedly null payload field). */
function toObjectPropertiesPatch(data: updateIssueTitleMutation$data): ObjectPropertiesPatch | undefined {
  const issue = data.updateIssue?.issue
  if (!issue) return undefined
  return {id: issue.id, values: {title: issue.title, titleHTML: issue.titleHTML}}
}

function toIssueTitleOperation(data: updateIssueTitleMutation$data): ConnectionOperation | undefined {
  const issue = data.updateIssue?.issue
  if (!issue) return undefined
  return {
    type: 'setFields',
    id: issue.id,
    fields: {title: issue.title, titleHTML: issue.titleHTML},
  }
}

export function commitUpdateIssueTitleMutation({
  environment,
  input: {issueId, title},
  onError,
  onCompleted,
}: {
  environment: Environment
  input: {issueId: string; title: string | null}
  onError?: (error: Error) => void
  onCompleted?: (response: updateIssueTitleMutation$data) => void
}) {
  const inputHash: UpdateIssueInput = {
    id: issueId,
  }
  if (title !== null) {
    inputHash.title = title
  }

  const optimisticResponse: updateIssueTitleMutation$data = {
    updateIssue: {
      issue: {
        id: issueId,
        title: title || '',
        titleHTML: escapeOptimisticTitleHTML(title || ''),
      },
    },
  }

  // Constructing the optimistic event up front (rather than dispatching it immediately)
  // lets its `mutationId` be generated by the `MutationLifecycleEvent` constructor, then
  // reused below for the completed/error events so all three correlate to this same
  // commitUpdateIssueTitleMutation call. `createMutationId()` is only a fallback for the
  // (practically unreachable, since `issueId` is always set) case where there's no node
  // to build an optimistic patch from.
  const optimisticPatch = toObjectPropertiesPatch(optimisticResponse)
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()
  const optimisticOperation = title !== null ? toIssueTitleOperation(optimisticResponse) : undefined

  const dispatchTerminalError = (error: Error) => {
    dispatchMutationLifecycleEvent(
      new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
    )
    dispatchConnectionMutation({phase: 'error', mutationId, error})
  }

  if (optimisticEvent) dispatchMutationLifecycleEvent(optimisticEvent)
  if (optimisticOperation) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId,
      ownerId: issueId,
      connectionPath: DOCUMENT_FIELDS_PATH,
      operation: optimisticOperation,
    })
  }

  let disposable
  try {
    disposable = commitMutation<updateIssueTitleMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation updateIssueTitleMutation($input: UpdateIssueInput!) @raw_response_type {
          updateIssue(input: $input) {
            issue {
              id
              title
              titleHTML
            }
          }
        }
      `,
      variables: {
        input: inputHash,
      },
      onError: error => {
        dispatchTerminalError(error)
        onError?.(error)
      },
      onUnsubscribe: () => {
        dispatchTerminalError(new Error('Updating the issue title was cancelled'))
      },
      optimisticResponse,
      onCompleted: (response, errors) => {
        const patch = toObjectPropertiesPatch(response)
        const operation = title !== null ? toIssueTitleOperation(response) : undefined
        if (!patch || (title !== null && !operation)) {
          const error = new Error(errors?.[0]?.message ?? 'Failed to update issue title')
          dispatchTerminalError(error)
          onError?.(error)
          return
        }
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
        if (operation) {
          dispatchConnectionMutation({
            phase: 'completed',
            mutationId,
            ownerId: issueId,
            connectionPath: DOCUMENT_FIELDS_PATH,
            operation,
          })
        }
        onCompleted?.(response)
      },
    })
  } catch (error) {
    dispatchTerminalError(error instanceof Error ? error : new Error('Failed to update issue title'))
    throw error
  }

  return disposable
}
